/**
 * DesignModelManager — Phase 1.
 *
 * Tab 4 of Scan-to-Model. Lets users:
 *   - Create model metadata + upload file (RVT/IFC/DWG/NWD/STP/FBX/OBJ/GLB)
 *   - Watch APS translation progress live (polls /translation-status every 8s)
 *   - List, filter, archive, upload new versions, view version chain
 *   - Inline-preview the translated SVF2 via the Panorix 3D viewer
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import ForgeViewerWrapper from '@/components/scan3d/ForgeViewerWrapper';
import {
  Upload, Eye, GitBranch, RefreshCw, Archive, X, Loader2, CheckCircle2,
  AlertCircle, Filter, Plus, Cuboid, Layers, ChevronRight, Building2, Compass,
} from 'lucide-react';
import CoordinateSystemModal from '@/components/scan3d/CoordinateSystemModal';

const ALLOWED_EXTS = ['rvt', 'ifc', 'dwg', 'nwd', 'stp', 'step', 'fbx', 'obj', 'glb', 'gltf', 'iges', 'igs'];
const DISCIPLINES = ['Structural', 'Piping', 'Electrical', 'Civil', 'Architectural', 'Mechanical', 'Process'];
const PHASES = ['IFC', 'IFD', 'AFC', 'As-Built'];
const STATUS_COLOR = {
  PENDING: '#94a3b8', TRANSLATING: '#f59e0b', SUCCESS: '#10b981',
  FAILED: '#ef4444', TIMEOUT: '#ef4444',
};

export default function DesignModelManager({ projectId }) {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ discipline: '', phase: '', area: '' });
  const [selectedId, setSelectedId] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [versionParent, setVersionParent] = useState(null); // model being version-bumped
  const [coordModel, setCoordModel] = useState(null); // model whose CRS is being edited
  const [chain, setChain] = useState([]);

  const fetchModels = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.discipline) params.set('discipline', filters.discipline);
      if (filters.phase) params.set('phase', filters.phase);
      if (filters.area) params.set('area', filters.area);
      const r = await api.get(`/v1/projects/${projectId}/scan3d/models?${params.toString()}`);
      setModels(r.data?.models || []);
    } catch (e) {
      toast.error('Failed to load models');
    } finally {
      setLoading(false);
    }
  }, [projectId, filters]);

  useEffect(() => { fetchModels(); }, [fetchModels]);

  // Poll translation status as a fallback (covers the WebSocket-disconnected case)
  useEffect(() => {
    if (!projectId) return;
    const translating = models.filter(m => m.translationStatus === 'TRANSLATING');
    if (translating.length === 0) return;
    const id = setInterval(async () => {
      for (const m of translating) {
        try {
          const r = await api.get(`/v1/projects/${projectId}/scan3d/models/${m.id}/translation-status`);
          setModels(prev => prev.map(p => p.id === m.id ? { ...p, ...r.data } : p));
        } catch (_) { /* ignore */ }
      }
    }, 30000);  // 30s fallback — WS handles real-time
    return () => clearInterval(id);
  }, [models, projectId]);

  // WebSocket — receive live translation + OBJ status pushes from the backend poller
  useEffect(() => {
    if (!projectId) return undefined;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = base.replace(/^http/, 'ws') + `/api/scan3d/jobs/ws?projectId=${projectId}`;
    let ws;
    let reconnectTimer;
    let stopped = false;
    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.kind === 'modelTranslation' && msg.modelId) {
              setModels(prev => prev.map(p => p.id === msg.modelId ? {
                ...p,
                translationStatus: msg.status,
                translationProgress: msg.progress,
                translationErrorMessage: msg.errorMessage,
              } : p));
            } else if (msg.kind === 'objStatus' && msg.modelId) {
              setModels(prev => prev.map(p => p.id === msg.modelId ? {
                ...p,
                objExportStatus: msg.objExportStatus,
                ...(msg.objMeshReady ? { objMeshReady: true } : {}),
              } : p));
              if (msg.objMeshReady) {
                toast.success(`Mesh ready — ${msg.modelCode}: real point-to-mesh deviation is now available`);
              }
            }
          } catch (_) { /* ignore */ }
        };
        ws.onclose = () => {
          if (!stopped) reconnectTimer = setTimeout(connect, 5000);
        };
      } catch (_) {
        if (!stopped) reconnectTimer = setTimeout(connect, 5000);
      }
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      if (ws) { ws.onclose = null; try { ws.close(); } catch {} }
    };
  }, [projectId]);

  const selected = useMemo(() => models.find(m => m.id === selectedId), [models, selectedId]);

  // Version chain
  useEffect(() => {
    if (!selectedId) { setChain([]); return; }
    api.get(`/v1/projects/${projectId}/scan3d/models/${selectedId}/versions`)
      .then(r => setChain(r.data?.versions || []))
      .catch(() => setChain([]));
  }, [selectedId, projectId]);

  const handleArchive = async (m) => {
    if (!window.confirm(`Archive ${m.modelCode} – ${m.modelName}?`)) return;
    try {
      await api.delete(`/v1/projects/${projectId}/scan3d/models/${m.id}`);
      toast.success('Archived');
      fetchModels();
      if (selectedId === m.id) setSelectedId(null);
    } catch { toast.error('Archive failed'); }
  };

  const handleRetranslate = async (m) => {
    try {
      await api.post(`/v1/projects/${projectId}/scan3d/models/${m.id}/retranslate`);
      toast.success('Translation re-queued');
      fetchModels();
    } catch { toast.error('Retranslate failed'); }
  };

  return (
    <div className="space-y-4" data-testid="scan3d-design-models-tab">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setShowUpload(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition active:scale-95"
          style={{ background: '#3b82f6', color: '#fff' }}
          data-testid="design-model-upload-btn">
          <Plus className="w-3.5 h-3.5" /> Upload Model
        </button>
        <div className="flex items-center gap-1.5 ml-1">
          <Filter className="w-3.5 h-3.5 opacity-50" />
          <select value={filters.discipline} onChange={e => setFilters(f => ({ ...f, discipline: e.target.value }))}
            className="px-2 py-1 rounded text-xs" style={{ background: '#111827', color: '#e5e7eb', border: '1px solid #1f2937' }}
            data-testid="design-model-filter-discipline">
            <option value="">All disciplines</option>
            {DISCIPLINES.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filters.phase} onChange={e => setFilters(f => ({ ...f, phase: e.target.value }))}
            className="px-2 py-1 rounded text-xs" style={{ background: '#111827', color: '#e5e7eb', border: '1px solid #1f2937' }}>
            <option value="">All phases</option>
            {PHASES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <input value={filters.area} onChange={e => setFilters(f => ({ ...f, area: e.target.value }))}
            placeholder="Area"
            className="px-2 py-1 rounded text-xs w-24" style={{ background: '#111827', color: '#e5e7eb', border: '1px solid #1f2937' }} />
        </div>
        <button onClick={fetchModels} className="ml-auto p-1.5 rounded" style={{ background: '#111827', color: '#9ca3af' }}>
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Model list + detail split */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-7 rounded-xl overflow-hidden" style={{ background: '#0f1722', border: '1px solid #1f2937' }}>
          <table className="w-full text-xs" data-testid="design-model-table">
            <thead style={{ background: '#0a0e14' }}>
              <tr>
                {['Code', 'Name', 'Discipline', 'Area', 'Phase', 'Status', 'Ver', ''].map(h => (
                  <th key={h} className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px] opacity-60">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {models.length === 0 && !loading && (
                <tr><td colSpan={8} className="text-center py-12 opacity-60">
                  <Cuboid className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  No design models yet. Upload one to start.
                </td></tr>
              )}
              {models.map(m => {
                const statusBg = STATUS_COLOR[m.translationStatus] || '#64748b';
                return (
                  <tr key={m.id}
                    className={`cursor-pointer hover:bg-white/5 ${selectedId === m.id ? 'bg-white/[0.07]' : ''}`}
                    onClick={() => setSelectedId(m.id)}
                    data-testid={`design-model-row-${m.modelCode}`}>
                    <td className="px-3 py-2 font-mono opacity-80">{m.modelCode}</td>
                    <td className="px-3 py-2 font-medium">{m.modelName}</td>
                    <td className="px-3 py-2 opacity-80">{m.discipline}</td>
                    <td className="px-3 py-2 opacity-80">{m.area || '—'}</td>
                    <td className="px-3 py-2 opacity-80">{m.phase}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        style={{ background: statusBg + '22', color: statusBg }}>
                        {m.translationStatus === 'TRANSLATING' ? <Loader2 className="w-3 h-3 animate-spin" /> :
                         m.translationStatus === 'SUCCESS' ? <CheckCircle2 className="w-3 h-3" /> :
                         m.translationStatus === 'FAILED' ? <AlertCircle className="w-3 h-3" /> :
                         <Layers className="w-3 h-3" />}
                        {m.translationStatus}
                        {m.translationStatus === 'TRANSLATING' && ` ${m.translationProgress || 0}%`}
                      </span>
                    </td>
                    <td className="px-3 py-2 opacity-80">v{m.versionNumber}</td>
                    <td className="px-3 py-2 text-right"><ChevronRight className="w-3.5 h-3.5 opacity-50" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Detail panel */}
        <div className="col-span-12 lg:col-span-5">
          {!selected && (
            <div className="rounded-xl p-6 text-center text-sm opacity-60" style={{ background: '#0f1722', border: '1px solid #1f2937' }}>
              Select a model to view details, preview & manage versions.
            </div>
          )}
          {selected && (
            <div className="rounded-xl p-4 space-y-3" style={{ background: '#0f1722', border: '1px solid #1f2937' }} data-testid="design-model-detail">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider opacity-60">{selected.modelCode} · v{selected.versionNumber}</div>
                  <div className="text-base font-semibold">{selected.modelName}</div>
                  {selected.description && <div className="text-xs opacity-70 mt-1">{selected.description}</div>}
                </div>
                <div className="flex items-center gap-1">
                  {selected.translationStatus === 'FAILED' && (
                    <button onClick={() => handleRetranslate(selected)} title="Retranslate"
                      className="p-1.5 rounded" style={{ background: '#0a0e14', color: '#f59e0b' }}>
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => setCoordModel(selected)} title="Coordinate system"
                    className="p-1.5 rounded" style={{ background: '#0a0e14', color: '#06b6d4' }}
                    data-testid="design-model-coord-btn">
                    <Compass className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setVersionParent(selected)} title="Upload new version"
                    className="p-1.5 rounded" style={{ background: '#0a0e14', color: '#3b82f6' }}
                    data-testid="design-model-new-version-btn">
                    <GitBranch className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleArchive(selected)} title="Archive"
                    className="p-1.5 rounded" style={{ background: '#0a0e14', color: '#ef4444' }}>
                    <Archive className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 rounded" style={{ background: '#0a0e14' }}>
                  <div className="opacity-60 text-[10px] uppercase">File</div>
                  <div className="font-medium truncate">{selected.originalFileName || '—'}</div>
                  <div className="text-[10px] opacity-50">{selected.fileSizeMB || 0} MB · {(selected.originalFormat || '—').toUpperCase()}</div>
                </div>
                <div className="p-2 rounded" style={{ background: '#0a0e14' }}>
                  <div className="opacity-60 text-[10px] uppercase">Translation</div>
                  <div className="font-medium" style={{ color: STATUS_COLOR[selected.translationStatus] }}>{selected.translationStatus}</div>
                  <div className="text-[10px] opacity-50">{selected.translationProgress || 0}%</div>
                </div>
                <div className="p-2 rounded" style={{ background: '#0a0e14' }}>
                  <div className="opacity-60 text-[10px] uppercase">Units</div>
                  <div className="font-medium">{selected.modelMetadata?.units || '—'}</div>
                </div>
                <div className="p-2 rounded" style={{ background: '#0a0e14' }}>
                  <div className="opacity-60 text-[10px] uppercase">Objects</div>
                  <div className="font-medium">{(selected.modelMetadata?.objectCount || 0).toLocaleString()}</div>
                </div>
              </div>
              {selected.translationStatus === 'FAILED' && selected.translationErrorMessage && (
                <div className="text-xs p-2 rounded" style={{ background: '#ef444415', color: '#ef4444' }}>
                  ⚠ {selected.translationErrorMessage}
                </div>
              )}
              {/* Version chain */}
              {chain.length > 1 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1.5 flex items-center gap-1"><Building2 className="w-3 h-3" /> Version History</div>
                  <div className="space-y-1">
                    {chain.map(v => (
                      <div key={v.id} className={`px-2 py-1.5 rounded text-xs flex items-center justify-between ${v.id === selected.id ? 'bg-white/[0.07]' : ''}`}
                        style={{ background: v.id === selected.id ? undefined : '#0a0e14' }}>
                        <span className="font-medium">v{v.versionNumber} {v.id === selected.id && '(current)'}</span>
                        <span className="opacity-60 text-[10px]">{v.uploadedAt ? new Date(v.uploadedAt).toLocaleDateString() : 'Not uploaded'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Forge preview */}
              {selected.translationStatus === 'SUCCESS' && selected.svf2Urn && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1.5 flex items-center gap-1"><Eye className="w-3 h-3" /> 3D Preview</div>
                  <ForgeViewerWrapper projectId={projectId} urn={selected.svf2Urn} height={320} testId="design-model-forge-preview" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {(showUpload || versionParent) && (
        <UploadModal
          projectId={projectId}
          versionParent={versionParent}
          onClose={() => { setShowUpload(false); setVersionParent(null); }}
          onCreated={() => { setShowUpload(false); setVersionParent(null); fetchModels(); }}
        />
      )}

      {/* Coordinate System Modal */}
      {coordModel && (
        <CoordinateSystemModal
          projectId={projectId}
          model={coordModel}
          onClose={() => setCoordModel(null)}
          onSaved={() => fetchModels()}
        />
      )}
    </div>
  );
}

function UploadModal({ projectId, versionParent, onClose, onCreated }) {
  const [form, setForm] = useState({
    modelName: versionParent?.modelName || '',
    description: versionParent?.description || '',
    discipline: versionParent?.discipline || 'Structural',
    area: versionParent?.area || '',
    phase: versionParent?.phase || 'IFC',
    versionNotes: '',
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const submit = async () => {
    if (!form.modelName.trim()) { toast.error('Model name required'); return; }
    if (!file) { toast.error('Pick a model file'); return; }
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      toast.error(`Unsupported format .${ext}. Allowed: ${ALLOWED_EXTS.join(', ')}`);
      return;
    }
    setUploading(true);
    try {
      let modelId;
      if (versionParent) {
        const r = await api.post(`/v1/projects/${projectId}/scan3d/models/${versionParent.id}/new-version`, form);
        modelId = r.data.id;
      } else {
        const r = await api.post(`/v1/projects/${projectId}/scan3d/models`, form);
        modelId = r.data.id;
      }

      // Files > 50 MB use the chunked endpoint to bypass ingress + memory limits;
      // smaller files keep the single-shot multipart for lower latency.
      const CHUNK_THRESHOLD = 50 * 1024 * 1024;
      const CHUNK_SIZE = 5 * 1024 * 1024;
      if (file.size > CHUNK_THRESHOLD) {
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const blob = file.slice(start, end);
          const fd = new FormData();
          // Preserve original filename so the server can derive the extension
          fd.append('file', new File([blob], file.name));
          await api.post(
            `/v1/projects/${projectId}/scan3d/models/${modelId}/upload-chunk?chunk_index=${i}&total_chunks=${totalChunks}`,
            fd,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 10 * 60 * 1000,
            }
          );
          // Progress = chunks uploaded
          setProgress(Math.round(((i + 1) / totalChunks) * 100));
        }
      } else {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(
          `/v1/projects/${projectId}/scan3d/models/${modelId}/upload`,
          fd,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => {
              if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
            },
            timeout: 30 * 60 * 1000,
          }
        );
      }
      toast.success(versionParent ? 'New version uploaded — translating now' : 'Model uploaded — translating now');
      onCreated();
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }} data-testid="design-model-upload-modal">
      <div className="w-full max-w-lg rounded-2xl p-5 space-y-3" style={{ background: '#0f1722', border: '1px solid #1f2937', color: '#e5e7eb' }}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-base font-bold">{versionParent ? `New Version: ${versionParent.modelName}` : 'Upload Design Model'}</div>
            {versionParent && <div className="text-xs opacity-60">v{versionParent.versionNumber} → v{versionParent.versionNumber + 1}</div>}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <label className="col-span-2">
            <span className="opacity-60">Model name *</span>
            <input value={form.modelName} onChange={e => setForm(f => ({ ...f, modelName: e.target.value }))}
              className="w-full mt-1 px-2 py-1.5 rounded" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
              data-testid="model-name-input" />
          </label>
          <label className="col-span-2">
            <span className="opacity-60">Description</span>
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full mt-1 px-2 py-1.5 rounded" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }} />
          </label>
          <label>
            <span className="opacity-60">Discipline</span>
            <select value={form.discipline} onChange={e => setForm(f => ({ ...f, discipline: e.target.value }))}
              className="w-full mt-1 px-2 py-1.5 rounded" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}>
              {DISCIPLINES.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
          <label>
            <span className="opacity-60">Phase</span>
            <select value={form.phase} onChange={e => setForm(f => ({ ...f, phase: e.target.value }))}
              className="w-full mt-1 px-2 py-1.5 rounded" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}>
              {PHASES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label>
            <span className="opacity-60">Area</span>
            <input value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))}
              className="w-full mt-1 px-2 py-1.5 rounded" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }} />
          </label>
          <label>
            <span className="opacity-60">Version notes</span>
            <input value={form.versionNotes} onChange={e => setForm(f => ({ ...f, versionNotes: e.target.value }))}
              className="w-full mt-1 px-2 py-1.5 rounded" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }} />
          </label>
        </div>
        {/* File picker */}
        <label className="block rounded-xl border-2 border-dashed py-6 text-center cursor-pointer"
          style={{ borderColor: file ? '#10b981' : '#1f2937', color: file ? '#10b981' : '#6b7280' }}
          data-testid="model-file-dropzone">
          <Upload className="w-6 h-6 mx-auto mb-1 opacity-70" />
          <div className="text-sm">{file ? file.name : 'Choose model file'}</div>
          <div className="text-[10px] opacity-50 mt-1">RVT · IFC · DWG · NWD · STP · FBX · OBJ · GLB · IGES (max 2 GB)</div>
          <input type="file" hidden onChange={e => setFile(e.target.files?.[0] || null)} accept={ALLOWED_EXTS.map(e => '.' + e).join(',')} />
        </label>
        {uploading && (
          <div className="space-y-1">
            <div className="h-1.5 rounded overflow-hidden" style={{ background: '#0a0e14' }}>
              <div className="h-full transition-all" style={{ width: `${progress}%`, background: '#3b82f6' }} />
            </div>
            <div className="text-[10px] opacity-60">
              {file && file.size > 50 * 1024 * 1024 ? 'Uploading in 5 MB chunks' : 'Uploading'} {progress}% — 3D translation will begin automatically
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} disabled={uploading} className="px-3 py-1.5 rounded text-xs" style={{ background: '#1f2937', color: '#9ca3af' }}>Cancel</button>
          <button onClick={submit} disabled={uploading} className="px-3 py-1.5 rounded text-xs font-semibold" style={{ background: '#3b82f6', color: '#fff' }} data-testid="design-model-submit-btn">
            {uploading ? <><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Uploading…</> : (versionParent ? 'Upload Version' : 'Upload & Translate')}
          </button>
        </div>
      </div>
    </div>
  );
}
